const TAG_ALIASES: Record<string, string> = {
  b: 'strong',
  i: 'em',
  strike: 's',
};

const BLOCK_TAGS = new Set(['blockquote', 'div', 'h1', 'h2', 'h3', 'li', 'ol', 'p', 'ul']);
const REMOVE_WITH_CONTENT_TAGS = new Set(['script', 'style']);
const TEXT_ALIGN_VALUES = new Set(['left', 'center', 'right', 'justify']);

export function sanitizeHTML(html: string | null | undefined, allowedTags: ReadonlyArray<string>) {
  if (!html) {
    return '';
  }

  const parser = new DOMParser();
  const documentNode = parser.parseFromString(html, 'text/html');
  const output = document.implementation.createHTMLDocument('rich-text-field');
  const wrapper = output.createElement('div');

  Array.from(documentNode.body.childNodes).forEach((node) => {
    const sanitizedNodes = sanitizeNode(node, output, allowedTags);

    sanitizedNodes.forEach((sanitizedNode) => {
      wrapper.appendChild(sanitizedNode);
    });
  });

  return wrapper.innerHTML;
}

function sanitizeNode(
  node: Node,
  output: Document,
  allowedTags: ReadonlyArray<string>
): Array<Node> {
  if (Node.TEXT_NODE === node.nodeType) {
    return [output.createTextNode(node.textContent ?? '')];
  }

  if (Node.ELEMENT_NODE !== node.nodeType) {
    return [];
  }

  const element = node as HTMLElement;
  const originalTag = element.tagName.toLowerCase();

  if (REMOVE_WITH_CONTENT_TAGS.has(originalTag)) {
    return [];
  }

  const normalizedTag = TAG_ALIASES[originalTag] ?? originalTag;
  const children = Array.from(element.childNodes).flatMap((childNode) =>
    sanitizeNode(childNode, output, allowedTags)
  );

  if (!allowedTags.includes(normalizedTag)) {
    return children;
  }

  const sanitizedElement = output.createElement(normalizedTag);

  children.forEach((childNode) => {
    sanitizedElement.appendChild(childNode);
  });

  applyTextAlign(element, sanitizedElement, normalizedTag);

  return [sanitizedElement];
}

function applyTextAlign(source: HTMLElement, target: HTMLElement, tagName: string) {
  if (!BLOCK_TAGS.has(tagName)) {
    return;
  }

  const inlineAlignment = source.style.textAlign;
  const attributeAlignment = source.getAttribute('align');
  const alignment = inlineAlignment || attributeAlignment || '';

  if (TEXT_ALIGN_VALUES.has(alignment)) {
    target.style.textAlign = alignment;
  }
}
